import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const { token, user } = response.data;
    
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'admin-root' || user?.role === 'admin-sub';
  };

  const isBroker = () => {
    return user?.role === 'broker';
  };

  const isClient = () => {
    return user?.role === 'client';
  };

  const isViewer = () => {
    return user?.role === 'viewer';
  };

  // Returns true if the user can access the given tab key ('dashboard' | 'reports')
  // Viewers are restricted to their allowed_tabs; all other roles have full access
  const canAccessTab = (tabKey) => {
    if (!user) return false;
    if (user.role !== 'viewer') return true;
    if (!user.allowed_tabs || user.allowed_tabs.length === 0) return true;
    return user.allowed_tabs.includes(tabKey);
  };

  // Dashboard column visibility, optionally narrowed to a specific account row.
  //   - canSeeColumn(key)              → uses global permissions.dashboard.columns
  //   - canSeeColumn(key, accountId)   → resolves per-account override:
  //                                      account_overrides[accountId].columns
  //                                      ?? dashboard.columns ?? null (= all)
  // null/missing column list ⇒ no restriction (column visible).
  const canSeeColumn = (columnKey, accountId) => {
    if (!user) return false;
    if (user.role !== 'viewer') return true;
    const dash = user.permissions && user.permissions.dashboard;
    const baseCols = dash && Array.isArray(dash.columns) ? dash.columns : null;
    if (accountId !== undefined && accountId !== null) {
      const ov = dash && dash.account_overrides && dash.account_overrides[String(accountId)];
      const cols = ov && Array.isArray(ov.columns) ? ov.columns : baseCols;
      if (!Array.isArray(cols)) return true;
      return cols.includes(columnKey);
    }
    if (!Array.isArray(baseCols)) return true;
    return baseCols.includes(columnKey);
  };

  // Resolve effective Dashboard columns for an account (per-account override
  // wins over global). Returns null when no restriction applies.
  const effectiveDashboardColumnsForAccount = (accountId) => {
    if (!user || user.role !== 'viewer') return null;
    const dash = user.permissions && user.permissions.dashboard;
    const baseCols = dash && Array.isArray(dash.columns) ? dash.columns : null;
    const ov = dash && dash.account_overrides && dash.account_overrides[String(accountId)];
    if (ov && Array.isArray(ov.columns)) return ov.columns;
    return baseCols;
  };

  // Header column set for the Dashboard table = union of effective columns
  // across all rendered rows. Returns null = "show every column" (when at least
  // one row has no restriction). Used to keep header/body in sync when accounts
  // have heterogeneous column rules.
  const unionDashboardColumns = (accounts) => {
    if (!user || user.role !== 'viewer') return null;
    const list = accounts || [];
    const set = new Set();
    for (const a of list) {
      const eff = effectiveDashboardColumnsForAccount(a.mt5_account_id);
      if (eff === null) return null;   // any account = "all" ⇒ header = all
      for (const k of eff) set.add(k);
    }
    return Array.from(set);
  };

  // Resolve effective Reports sub_tabs for a specific account (per-account
  // override wins over global). Returns null = no restriction.
  const effectiveReportSubTabsForAccount = (accountId) => {
    if (!user || user.role !== 'viewer') return null;
    const reports = user.permissions && user.permissions.reports;
    const baseSubs = reports && Array.isArray(reports.sub_tabs) ? reports.sub_tabs : null;
    const ov = reports && reports.account_overrides && reports.account_overrides[String(accountId)];
    if (ov && Array.isArray(ov.sub_tabs)) return ov.sub_tabs;
    return baseSubs;
  };

  // True if this account is allowed to appear in the given Reports sub-tab.
  // Used by the account dropdown filter inside each Reports sub-tab.
  const canAccountSeeReport = (accountId, subTabKey) => {
    if (!user || user.role !== 'viewer') return true;
    const subs = effectiveReportSubTabsForAccount(accountId);
    if (!Array.isArray(subs)) return true;
    return subs.includes(subTabKey);
  };

  // Whether viewer can open the EA config (Setup) popup for a specific account.
  // view_setup === false ⇒ disabled globally. setup_accounts whitelist ⇒ only listed
  // accounts. setup_accounts null/undefined ⇒ falls back to allowed_accounts.
  const canViewSetup = (accountId) => {
    if (!user) return false;
    if (user.role !== 'viewer') return true;
    const dash = user.permissions && user.permissions.dashboard;
    if (dash && dash.view_setup === false) return false;
    const whitelist = (dash && Array.isArray(dash.setup_accounts))
      ? dash.setup_accounts
      : (Array.isArray(user.allowed_accounts) ? user.allowed_accounts : null);
    if (!Array.isArray(whitelist)) return true;
    return whitelist.map(String).includes(String(accountId));
  };

  // Reports sub-tab visibility (top-level only — does not consider per-account
  // overrides). A sub-tab is considered visible if at least one allowed account
  // (or the global rule, when allowed_accounts is null) permits it.
  const canSeeReport = (subTabKey) => {
    if (!user) return false;
    if (user.role !== 'viewer') return true;
    const reports = user.permissions && user.permissions.reports;
    const baseSubs = reports && Array.isArray(reports.sub_tabs) ? reports.sub_tabs : null;
    const overrides = (reports && reports.account_overrides) || {};
    const accs = Array.isArray(user.allowed_accounts) ? user.allowed_accounts : null;

    if (accs === null) {
      // No per-account whitelist → only the global rule matters.
      return !Array.isArray(baseSubs) || baseSubs.includes(subTabKey);
    }
    // Visible if any allowed account grants this sub-tab.
    return accs.some(acc => {
      const ov = overrides[String(acc)];
      const subs = ov && Array.isArray(ov.sub_tabs) ? ov.sub_tabs : baseSubs;
      return !Array.isArray(subs) || subs.includes(subTabKey);
    });
  };

  // Resolve effective master_performance sub_tabs for an account (per-account
  // override wins over global). Returns null = no restriction.
  const effectiveMasterPerfSubTabsForAccount = (accountId) => {
    if (!user || user.role !== 'viewer') return null;
    const mp = user.permissions && user.permissions.master_performance;
    const baseSubs = mp && Array.isArray(mp.sub_tabs) ? mp.sub_tabs : null;
    const ov = mp && mp.account_overrides && mp.account_overrides[String(accountId)];
    if (ov && Array.isArray(ov.sub_tabs)) return ov.sub_tabs;
    return baseSubs;
  };

  // True if this account is allowed in the given Master Performance sub-tab.
  const canAccountSeeMasterPerfSubTab = (accountId, subTabKey) => {
    if (!user || user.role !== 'viewer') return true;
    const subs = effectiveMasterPerfSubTabsForAccount(accountId);
    if (!Array.isArray(subs)) return true;
    return subs.includes(subTabKey);
  };

  // Top-level visibility: a sub-tab is shown if at least one allowed account
  // permits it (mirrors canSeeReport for reports).
  const canSeeMasterPerfSubTab = (subTabKey) => {
    if (!user) return false;
    if (user.role !== 'viewer') return true;
    const mp = user.permissions && user.permissions.master_performance;
    const baseSubs = mp && Array.isArray(mp.sub_tabs) ? mp.sub_tabs : null;
    const overrides = (mp && mp.account_overrides) || {};
    const accs = Array.isArray(user.allowed_accounts) ? user.allowed_accounts : null;
    if (accs === null) {
      return !Array.isArray(baseSubs) || baseSubs.includes(subTabKey);
    }
    return accs.some(acc => {
      const ov = overrides[String(acc)];
      const subs = ov && Array.isArray(ov.sub_tabs) ? ov.sub_tabs : baseSubs;
      return !Array.isArray(subs) || subs.includes(subTabKey);
    });
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin,
    isBroker,
    isClient,
    isViewer,
    canAccessTab,
    canSeeColumn,
    canViewSetup,
    canSeeReport,
    effectiveDashboardColumnsForAccount,
    unionDashboardColumns,
    effectiveReportSubTabsForAccount,
    canAccountSeeReport,
    effectiveMasterPerfSubTabsForAccount,
    canAccountSeeMasterPerfSubTab,
    canSeeMasterPerfSubTab,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
